import { Link } from "react-router-dom";
import OakLogo from "@/components/OakLogo";
import InfraBadges, { INFRA_DISCLAIMER } from "@/components/InfraBadges";
import { useT } from "@/hooks/useT";
import { buildLabel } from "@/lib/buildVersion";

export default function Footer() {
  const { t } = useT();
  return (
    <footer className="mt-24 border-t border-white/10 bg-[#0B0E14]/60" data-testid="app-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-3 gap-12">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <OakLogo size={36} />
            <div>
              <div className="font-display font-semibold text-lg">Oak AI Campus</div>
              <div className="label-eyebrow !text-[10px]">{t("footer_tagline")}</div>
            </div>
          </div>
          <p className="text-sm text-white/50 mt-2 max-w-sm">
            {t("footer_blurb")}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <div className="label-eyebrow">{t("footer_legal")}</div>
          <Link to="/privacy" className="text-white/70 hover:text-white text-sm" data-testid="link-privacy">
            {t("footer_privacy")}
          </Link>
          <Link to="/terms" className="text-white/70 hover:text-white text-sm" data-testid="link-terms">
            {t("footer_terms")}
          </Link>
          <Link to="/refund" className="text-white/70 hover:text-white text-sm" data-testid="link-refund">
            {t("footer_refund")}
          </Link>
          <Link to="/settings" className="text-white/70 hover:text-white text-sm" data-testid="link-settings">
            {t("footer_settings")}
          </Link>
        </div>

        <div className="flex flex-col gap-3">
          <div className="label-eyebrow">{t("footer_contact")}</div>
          <a
            href="mailto:info@oakaiapp.com"
            className="text-white/70 hover:text-white text-sm"
            data-testid="link-email"
          >
            info@oakaiapp.com
          </a>
          <div className="text-white/50 text-sm">oakaiapp.com</div>
        </div>
      </div>

      {/* Infrastructure & Standards bar — sits above the legal line on
          every page. Monochrome badges + mandatory trademark
          attribution per the institutional pivot brief. */}
      <div
        className="border-t border-white/5 px-4 py-5"
        data-testid="footer-infra-bar"
      >
        <div className="max-w-3xl mx-auto flex flex-col items-center gap-3">
          <div className="label-eyebrow !text-[9px] tracking-[0.4em] text-white/35">
            Infrastructure &amp; Standards
          </div>
          <InfraBadges />
          <p className="text-[10px] text-white/40 leading-relaxed text-center max-w-2xl mt-1 font-mono-oak">
            {INFRA_DISCLAIMER}
          </p>
        </div>
      </div>

      <div
        className="border-t border-white/5 py-6 text-center text-xs text-white/55 tracking-wide leading-relaxed px-4"
        data-testid="footer-ownership"
      >
        <div className="max-w-3xl mx-auto">
          © {new Date().getFullYear()}{" "}
          <span className="text-white/85 font-medium">
            Digiluxcon Technology Services (OPC) Private Limited
          </span>
          . Oak AI Campus is owned and operated by Digiluxcon Technology Services (OPC) Private
          Limited. Conceptualized by{" "}
          <span className="text-white/85 font-medium">Abhishek Singh</span>.
        </div>
        <div className="text-white/35 mt-1.5 text-[10px] tracking-wider uppercase font-mono-oak">
          Oak AI Campus · {t("footer_rights")}
          <span className="mx-2 text-white/15">·</span>
          <span data-testid="footer-build-version" title="Production build version — bump on every redeploy">
            {buildLabel()}
          </span>
        </div>
      </div>
    </footer>
  );
}
